// RegisterPage - User registration
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Anchor,
  Divider,
  Center,
  Progress,
  Box,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconMail, IconLock, IconUser, IconBrandGoogle } from '@tabler/icons-react';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';

// Password strength calculator
const getPasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  return strength;
};

const getPasswordColor = (strength: number): string => {
  if (strength < 50) return 'red';
  if (strength < 75) return 'yellow';
  return 'green';
};

export const RegisterPage = () => {
  const { register, isRegistering } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: {
      name: (value) => {
        if (!value) return 'Name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        return null;
      },
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email address';
        return null;
      },
      password: (value) => {
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
        if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
        if (!/[0-9]/.test(value)) return 'Password must contain a number';
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return 'Please confirm your password';
        if (value !== values.password) return 'Passwords do not match';
        return null;
      },
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      await register({
        name: values.name,
        email: values.email,
        password: values.password,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordStrength = getPasswordStrength(form.values.password);

  return (
    <Container size={460} my={40}>
      <Center mb="xl">
        <Title order={1} style={{ color: '#2196f3' }}>
          CarLeads
        </Title>
      </Center>

      <Title ta="center" order={2} mb="md">
        Create your account
      </Title>

      <Text c="dimmed" size="sm" ta="center" mb="xl">
        Start managing your automotive leads today
      </Text>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Full Name"
              placeholder="John Doe"
              leftSection={<IconUser size={16} />}
              required
              {...form.getInputProps('name')}
              disabled={isSubmitting || isRegistering}
            />

            <TextInput
              label="Email"
              placeholder="your@email.com"
              leftSection={<IconMail size={16} />}
              required
              {...form.getInputProps('email')}
              disabled={isSubmitting || isRegistering}
            />

            <Box>
              <PasswordInput
                label="Password"
                placeholder="Create a strong password"
                leftSection={<IconLock size={16} />}
                required
                {...form.getInputProps('password')}
                disabled={isSubmitting || isRegistering}
              />
              {form.values.password && (
                <Box mt="xs">
                  <Progress
                    value={passwordStrength}
                    color={getPasswordColor(passwordStrength)}
                    size="sm"
                  />
                  <Text size="xs" c="dimmed" mt={4}>
                    Password strength: {passwordStrength < 50 ? 'Weak' : passwordStrength < 75 ? 'Medium' : 'Strong'}
                  </Text>
                </Box>
              )}
            </Box>

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your password"
              leftSection={<IconLock size={16} />}
              required
              {...form.getInputProps('confirmPassword')}
              disabled={isSubmitting || isRegistering}
            />

            <Button
              type="submit"
              fullWidth
              loading={isSubmitting || isRegistering}
              size="md"
            >
              Create account
            </Button>

            <Divider label="or continue with" labelPosition="center" />

            <Button
              variant="default"
              leftSection={<IconBrandGoogle size={16} />}
              fullWidth
              disabled
            >
              Google (Coming soon)
            </Button>

            <Text c="dimmed" size="sm" ta="center">
              Already have an account?{' '}
              <Anchor component={Link} to={ROUTES.LOGIN} fw={500}>
                Sign in
              </Anchor>
            </Text>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};
